import { useCallback, useMemo } from 'react';
import { V12PaymentRequest } from 'shared/types/payment/V12PaymentOption';
import { mutate } from 'swr';
import * as uuid from 'uuid';
import { useAdyenCheckout } from 'components/commercetools-ui/organisms/checkout/provider';
import { PaymentType } from 'components/commercetools-ui/organisms/checkout/provider/payment/types';
import useLocale from 'helpers/hooks/useLocale';
import { encrypt } from 'helpers/utils/cryptoJs';
import { Guid } from 'helpers/utils/guid';
import { sdk } from 'sdk';
import { useAccount, useCart, useV12 } from 'frontastic';
import useGivex from 'frontastic/hooks/useGivex';
import { SessionRequestData } from '../type';

const PaymentHelper = () => {
  const { freezeCart, data, unfreezeCart } = useCart();

  const { getCountry, getCurrency, locale } = useLocale();
  const orderNumber = Guid.newGuid(false, ['', 'xxxx-xxxx-yxxx']);
  const { account } = useAccount();
  const {
    paymentStatus,
    setPaymentStatus,
    adyenCheckoutInstance,
    setIsPaymentDataValid,
    setAdyenCheckoutInstanceState,
    selectedPaymentMethod,
    setProcessing,
    v12PaymentData,
    setSelectedPaymentMethod,
    paymentSession,
  } = useAdyenCheckout();
  const { placeOrder, preauthGiftcards, cancelPreauthGiftcards } = useGivex();
  const { intiatePayment } = useV12();

  const areAllGivexPayments = useMemo(
    () => data?.sum?.centAmount === 0 && data?.payments && data?.payments?.length > 0,
    [data?.sum, data?.payments],
  );

  const containSplitGivexPayments = useMemo(() => {
    const hasGivexPayments = data?.payments && data?.payments.some((payment) => payment.giftcardNumber);
    return data?.sum?.centAmount && data?.sum?.centAmount > 0 && hasGivexPayments;
  }, [data?.sum, data?.payments]);

  const hasGiftcard = useMemo(() => {
    if (data?.lineItems === undefined) {
      return false;
    }

    for (let i = 0; i < data.lineItems.length; i++) {
      if (data.lineItems[i].isGiftcard === true) {
        return true;
      }
    }
    return false;
  }, [data?.lineItems]);

  const freezeCartForPayment = async (data: any) => {
    if (data?.cartState !== 'Frozen') {
      await freezeCart(data?.cartId);
    }
  };

  const sessionInitializationRequest = async (data: any) => {
    const countryCode = getCountry();
    const currencyCode = getCurrency();
    const amountValue = data?.sum.centAmount ?? 0;
    const isBrowser = typeof window !== 'undefined';

    let returnUrl = '';

    if (isBrowser) {
      const redirectPaymentMethods = ['ideal', 'clearpay', 'klarna', 'klarna_account', 'klarna_paynow'];
      if (selectedPaymentMethod && redirectPaymentMethods.includes(selectedPaymentMethod)) {
        const ctSession = encrypt(
          JSON.stringify({
            orderId: orderNumber,
            mode: selectedPaymentMethod,
            cartId: data?.cartId ?? '',
            cartTotal: amountValue,
          }),
        );
        returnUrl = `${window.location.origin}/checkout/?ctSession=${ctSession}`;
      } else {
        sessionStorage.setItem('pushToDataLayer', 'true');
        returnUrl = `${window.location.origin}/thank-you/?orderId=${orderNumber}`;
      }
    }

    return {
      amount: {
        currency: currencyCode,
        value: amountValue,
      },
      shopperName: {
        firstName: data?.shippingAddress?.firstName || data?.billingAddress?.firstName,
        lastName: data?.shippingAddress?.lastName || data?.billingAddress?.lastName,
      },
      telephoneNumber: data?.shippingAddress?.phone || data?.billingAddress?.phone,
      returnUrl: returnUrl,
      reference: `${orderNumber}` as string,
      countryCode: data?.billingAddress.country ?? countryCode,
      shopperLocale: locale,
      shopperReference: account?.accountId ?? uuid.v4(),
      billingAddress: {
        houseNumberOrName: data?.billingAddress.additionalAddressInfo ?? '',
        street: data?.billingAddress.additionalAddressInfo ?? '',
        city: data?.billingAddress.city ?? '',
        country: data?.billingAddress.country ?? '',
        postalCode: data?.billingAddress.postalCode ?? '',
      },
      //Saved Card related confic
      // shopperInteraction: 'ContAuth',
      // storePaymentMethodMode: 'askForConsent',
      // recurringProcessingModel: 'CardOnFile',
      // storePaymentMethod: true,
      metadata: { cartId: data?.cartId ?? '' },
      deliveryAddress: {
        houseNumberOrName: data?.shippingAddress.additionalAddressInfo ?? '',
        street: data?.shippingAddress.streetName,
        city: data?.shippingAddress.city ?? '',
        country: data?.shippingAddress.country ?? '',
        postalCode: data?.shippingAddress.postalCode ?? '',
      },
      shopperEmail: data?.email,
      channel: 'Web',
      lineItems: lineItems(),
    };
  };

  function lineItems() {
    const lineItems = data?.lineItems?.map((item, index) => ({
      quantity: item.count,
      description: item.name,
      id: `Item #${index + 1}`,
      amountIncludingTax: item.price?.centAmount,
    }));
    return lineItems;
  }

  const onPaymentCompleted = async (response: any, _component: any, router: any, sessionRequestData: any) => {
    if (['Authorised', 'RedirectShopper', 'IdentifyShopper', 'ChallengeShopper'].includes(response.resultCode)) {
      const cart = (await sdk.callAction({
        actionName: 'cart/getCart',
      })) as any;

      const checkForCartTotalError =
        selectedPaymentMethod === 'paypal' ||
        (selectedPaymentMethod === 'scheme' && cart?.data?.billingAddress?.firstName);
      if (checkForCartTotalError) {
        if (cart?.data?.sum?.centAmount !== sessionRequestData?.amount?.value) {
          // mountedComponent?.remove(`#${selectedPaymentMethod}-container`);
          setPaymentStatus('cartTotalError');
          await mutate('/action/cart/getCart');
          return;
        }
      }

      setPaymentStatus('success');

      if (!response.action) {
        //const res = await sdk.callAction({ actionName: 'cart/resetCart' });
        // mutate('/action/cart/getCart', res);
        if (selectedPaymentMethod === 'paypal' && containSplitGivexPayments) {
          await preauthGiftcards();
        }
        const parts = sessionRequestData.reference.split('_');
        const orderId = parts[0];
        sessionStorage.setItem('pushToDataLayer', 'true');
        if (router) {
          router.push(`/thank-you/?orderId=${orderId}`);
        } else {
          window.location.assign(`/thank-you/?orderId=${orderId}`);
        }
        return;
      }

      switch (response.action.type) {
        case 'redirect':
          //const res = await sdk.callAction({ actionName: 'cart/resetCart' });
          // mutate('/action/cart/getCart', res);
          window.location.replace(response.action.url as string);
          break;
      }
    } else if (response.resultCode === 'Refused') {
      setPaymentStatus('refused');
      setSelectedPaymentMethod('');
      initCancelPreauthGiftcards();
    } else if (response.resultCode === 'Cancelled') {
      setPaymentStatus('cancelled');
      setSelectedPaymentMethod('');
      initCancelPreauthGiftcards();
    } else {
      setPaymentStatus('error');
      setSelectedPaymentMethod('');
      initCancelPreauthGiftcards();
      // toast.error(
      //   `${formatCheckoutMessage({
      //     id: 'payment.failed',
      //     defaultMessage: 'We could not process your payment, please try again later.',
      //   })}`,
      // );
    }
    if (['Refused', 'Cancelled', 'error'].includes(response.resultCode) && data && data?.cartId) {
      await unfreezeCart(data?.cartId);
    }
  };

  const getConfig = (paymentMethod: string, sessionRequestData: SessionRequestData, router: any) => {
    // Common configuration
    const commonConfig = {
      locale: locale,
      environment: process.env.NEXT_PUBLIC_ADYEN_ENV,
      clientKey: process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY,
      session: sessionRequestData,
      showPayButton: true,
      onPaymentCompleted: (response: any, _component: any) =>
        onPaymentCompleted(response, _component, router, sessionRequestData),
      onError: (error: any, _component: any) => {
        // if (error.name == 'CANCEL' && paymentMethod === 'paypal') {
        //   setPaymentStatus('cancelled');
        // }
        // toast.error(
        //   `${formatCheckoutMessage({
        //     id: 'payment.failed',
        //     defaultMessage: 'We could not process your payment, please try again later.',
        //   })}`,
        // );
      },
    };

    // Conditional payment method configuration
    if (paymentMethod === 'scheme' || paymentMethod === 'bcmc') {
      setIsPaymentDataValid(false);
      return {
        ...commonConfig,
        onChange: (state: any, component: any) => {
          if (state.isValid && state.data && state.data.paymentMethod) {
            setIsPaymentDataValid(true);
            setAdyenCheckoutInstanceState(state);
            if (paymentStatus !== '' && paymentStatus !== 'error') {
              setPaymentStatus('');
            }
          }

          if (!state.isValid) {
            setIsPaymentDataValid(false);
          }
        },
        onActionHandled: (response: any) => {
          if (response.componentType.startsWith('3DS2')) {
            setProcessing(true);
            setPaymentStatus('3ds2');
          }
        },
        paymentMethodsConfiguration: {
          card: {
            hasHolderName: true,
            holderNameRequired: true,
            hasCVC: true,
            hideCVC: false,
            maskSecurityCode: true,
            showPayButton: false,
            positionHolderNameOnTop: true,

            showBrandsUnderCardNumber: true,
            showBrandIcon: true,
            showFormInstruction: true,
            isDropin: false,
            enableStoreDetails: false, // Save card config
            autoFocus: true,
          },
          bcmc: {
            showPayButton: false,
            cvcPolicy: 'required',
            hasHolderName: true,
            holderNameRequired: true,
            hasCVC: true,
            hideCVC: false,
            showBrandsUnderCardNumber: true,
            showBrandIcon: true,
            positionHolderNameOnTop: true,
            brands: ['mc', 'visa', 'meastro', 'bcmc'],
          },
        },
      };
    }
    if (paymentMethod === 'ideal') {
      return {
        ...commonConfig,
        onChange: (state: any, component: any) => {
          if (state.isValid && state.data && state.data.paymentMethod) {
            setIsPaymentDataValid(true);
            setAdyenCheckoutInstanceState(state);
            if (paymentStatus !== '' && paymentStatus !== 'error') {
              setPaymentStatus('');
            }
          }

          if (!state.isValid) {
            setIsPaymentDataValid(false);
          }
        },
        paymentMethodsConfiguration: {
          ideal: {
            showPayButton: false,
          },
        },
      };
    }
    if (paymentMethod === 'klarna') {
      return {
        ...commonConfig,
        paymentMethodsConfiguration: {
          klarna: {
            showPayButton: false,
          },
        },
      };
    }

    if (paymentMethod === 'klarna_account') {
      return {
        ...commonConfig,
        paymentMethodsConfiguration: {
          klarna_account: {
            showPayButton: false,
          },
        },
      };
    }
    if (paymentMethod === 'clearpay') {
      return {
        ...commonConfig,
        paymentMethodsConfiguration: {
          clearpay: {
            showPayButton: false,
          },
        },
      };
    }
    if (paymentMethod === 'klarna_paynow') {
      return {
        ...commonConfig,
        paymentMethodsConfiguration: {
          klarna_paynow: {
            showPayButton: false,
          },
        },
      };
    }
    if (paymentMethod === 'paypal') {
      return {
        ...commonConfig,
        paymentMethodsConfiguration: {
          paypal: {
            style: {
              color: 'black',
              label: 'pay',
              tagline: 'true',
              height: 40,
            },
            showPayButton: true,
            blockPayPalCreditButton: false,
            blockPayPalPayLaterButton: false,
          },
        },
      };
    } else {
      return commonConfig;
    }
  };

  const submitV12Payment = useCallback(async (request: V12PaymentRequest) => {
    intiatePayment(request)
      .then((response) => {
        if (response.redirectUrl !== undefined) {
          window.location.href = response.redirectUrl;
        }
      })
      .catch(() => {
        setPaymentStatus('error');
      });
  }, []);

  const submitGivexPayment = useCallback(async () => {
    placeOrder()
      .then(async (response) => {
        const orderNumber = response.orderNumber;
        //const res = await sdk.callAction({ actionName: 'cart/resetCart' });
        // mutate('/action/cart/getCart', res);
        setTimeout(() => {
          sessionStorage.setItem('pushToDataLayer', 'true');
          window.location.assign(`/thank-you/?orderId=${orderNumber}`);
        }, 10);
      })
      .catch(() => {
        setPaymentStatus('error');
      });
  }, []);

  const initCancelPreauthGiftcards = () => {
    if (containSplitGivexPayments) {
      cancelPreauthGiftcards();
    }
  };

  const submitPayment = async () => {
    const cart = (await sdk.callAction({ actionName: 'cart/getCart' })) as any;

    if (!areAllGivexPayments && !containSplitGivexPayments && selectedPaymentMethod !== PaymentType.V12) {
      // const freezeCarts = await freezeCart(cart?.data?.cartId);
    }

    if (areAllGivexPayments) {
      if (!hasGiftcard) {
        submitGivexPayment();
      }
      return;
    }

    if (selectedPaymentMethod === PaymentType.V12 && v12PaymentData?.productId) {
      const request = { ...v12PaymentData };
      submitV12Payment(request);
      return;
    }
    if (!selectedPaymentMethod || !adyenCheckoutInstance) {
      console.error('Payment method not selected or Adyen Checkout not initialized');
      return;
    }

    if (cart?.data?.sum?.centAmount !== paymentSession?.amount?.value) {
      // mountedComponent?.remove(`#${selectedPaymentMethod}-container`);
      setPaymentStatus('cartTotalError');
      await mutate('/action/cart/getCart');
      return;
    }

    const paymentMethodComponent = adyenCheckoutInstance.components[0];

    if (paymentMethodComponent) {
      if (containSplitGivexPayments) {
        if (!hasGiftcard) {
          try {
            await preauthGiftcards();
            paymentMethodComponent.submit();
          } catch (error) {
            setPaymentStatus('error');
            console.error('Givex preauthorization error:', error);
          }
        }
      } else {
        paymentMethodComponent.submit();
      }
    } else {
      console.error('Payment method component not found');
    }
  };

  return {
    sessionInitializationRequest,
    getConfig,
    onPaymentCompleted,
    submitPayment,
    freezeCartForPayment,
  };
};

export default PaymentHelper;
